<template>
  <div
    class="group flex items-center p-1.25 gap-1 rounded-ds-sm font-ds font-medium cursor-pointer transition-colors duration-ds-fast ease-ds-standard"
    :class="[
      isLastOne || readonly ? 'pl-3' : 'pl-2',
      active ? 'bg-card shadow-ds-xs text-foreground font-bold' : 'text-muted-foreground hover:bg-card hover:shadow-ds-xs hover:text-foreground',
    ]"
    :data-active="active ? 'true' : null"
  >
    <div
      v-if="isEditMode"
      ref="inputWrapRef"
      class="-m-1.25 min-w-38"
      :class="isLastOne || readonly ? '-ml-3' : '-ml-2'"
      @click.stop
    >
      <DSInput
        v-model="configName"
        size="sm"
        aria-label="Configuration name"
        class="h-auto p-1.25 border-none rounded-ds-sm!"
        :class="isLastOne || readonly ? 'pl-3' : 'pl-2'"
        @keydown.enter.prevent="isEditMode = false"
        @keydown.esc="isEditMode = false"
        @keydown.tab="isEditMode = false"
      />
    </div>
    <template v-else>
      <GripVertical
        v-if="!isLastOne && !readonly"
        class="size-3 shrink-0 cursor-grab text-muted-foreground/50 hover:text-muted-foreground"
        aria-hidden="true"
        data-handle="true"
        @click.stop
      />
      <DSTooltip v-if="isLabelEllipsis">
        <DSTooltipTrigger as-child>
          <span
            ref="labelRef"
            class="max-w-48 truncate text-base sm:text-sm"
          >{{ label }}</span>
        </DSTooltipTrigger>
        <DSTooltipContent>{{ label }}</DSTooltipContent>
      </DSTooltip>
      <span
        v-else
        ref="labelRef"
        class="max-w-48 truncate text-base sm:text-sm"
      >{{ label }}</span>
      <DSDropdownMenu v-if="!readonly">
        <DSDropdownMenuTrigger as-child>
          <DSButton
            variant="ghost"
            size="icon-sm"
            class="-my-0.5 size-6 shrink-0 sm:opacity-0 group-hover:sm:opacity-100 data-[state=open]:opacity-100"
            aria-label="Configuration actions"
            @click.stop
          >
            <MoreHorizontal class="size-4" />
          </DSButton>
        </DSDropdownMenuTrigger>
        <DSDropdownMenuContent align="start">
          <DSDropdownMenuItem @select="$emit('duplicate')">
            <Copy class="size-4" />
            Duplicate
          </DSDropdownMenuItem>
          <DSDropdownMenuItem @select="isEditMode = true">
            <Pencil class="size-4" />
            Rename
          </DSDropdownMenuItem>
          <template v-if="!isLastOne">
            <DSDropdownMenuSeparator />
            <DSDropdownMenuItem @select="$emit('pin')">
              <Paperclip class="size-4" />
              Pin to dashboards
            </DSDropdownMenuItem>
            <DSDropdownMenuItem
              class="text-destructive focus:bg-destructive-subtle focus:text-destructive"
              @select="confirmRemoveOpen = true"
            >
              <Trash2 class="size-4" />
              Remove
            </DSDropdownMenuItem>
          </template>
        </DSDropdownMenuContent>
      </DSDropdownMenu>
    </template>

    <DSDialog v-model:open="confirmRemoveOpen">
      <DSDialogContent>
        <DSDialogHeader>
          <DSDialogTitle>Delete this configuration?</DSDialogTitle>
          <DSDialogDescription>
            "{{ label }}" will be permanently removed. This cannot be undone.
          </DSDialogDescription>
        </DSDialogHeader>
        <DSDialogFooter>
          <DSButton
            variant="secondary"
            @click="confirmRemoveOpen = false"
          >
            Cancel
          </DSButton>
          <DSButton
            variant="destructive"
            @click="handleDeleteConfirm"
          >
            Remove
          </DSButton>
        </DSDialogFooter>
      </DSDialogContent>
    </DSDialog>
  </div>
</template>

<script setup lang="ts">
import { onMounted, nextTick, ref, useTemplateRef, watch } from 'vue'
import { Copy, GripVertical, MoreHorizontal, Paperclip, Pencil, Trash2 } from '@lucide/vue'
import { onClickOutside, useResizeObserver } from '@vueuse/core'

import {
  DSButton,
  DSDialog,
  DSDialogContent,
  DSDialogDescription,
  DSDialogFooter,
  DSDialogHeader,
  DSDialogTitle,
  DSDropdownMenu,
  DSDropdownMenuContent,
  DSDropdownMenuItem,
  DSDropdownMenuSeparator,
  DSDropdownMenuTrigger,
  DSInput,
  DSTooltip,
  DSTooltipContent,
  DSTooltipTrigger,
} from '@ds'

type Props = {
  label: string
  isLastOne: boolean
  active?: boolean
  editOnRender?: boolean
  readonly?: boolean
}

type Emits = {
  duplicate: []
  remove: []
  update: [data: string]
  pin: []
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const labelRef = useTemplateRef('labelRef')
const inputWrapRef = useTemplateRef('inputWrapRef')

const configName = ref('')
const isEditMode = ref(false)
const confirmRemoveOpen = ref(false)
const isLabelEllipsis = ref(false)

useResizeObserver(labelRef, () => {
  if (!props.label || !labelRef.value) return
  isLabelEllipsis.value = labelRef.value.offsetWidth < labelRef.value.scrollWidth
})

const handleDeleteConfirm = () => {
  confirmRemoveOpen.value = false
  emit('remove')
}

watch(isEditMode, async value => {
  if (value) {
    configName.value = props.label
    await nextTick()
    inputWrapRef.value?.querySelector('input')?.focus()
  } else if (configName.value !== props.label) {
    emit('update', configName.value)
  }
})

onClickOutside(inputWrapRef, () => {
  isEditMode.value = false
})

onMounted(() => {
  if (props.editOnRender) isEditMode.value = true
})
</script>
